import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const processPayment = async () => {
      const sessionId = searchParams.get('session_id');
      const requestId = searchParams.get('request_id');
      const trainerId = searchParams.get('trainer_id');
      const paymentSuccess = searchParams.get('payment') === 'success';

      // Check if this is a trainer registration payment
      if (trainerId && paymentSuccess) {
        try {
          console.log('Verifying trainer registration payment for trainer_id:', trainerId);
          const verifyResponse = await apiService.verifyTrainerRegistrationPayment({
            trainer_id: trainerId
          });

          console.log('Trainer payment verification response:', verifyResponse);

          if (verifyResponse.Code === 200 && verifyResponse.payment_status === 'completed') {
            setPaymentData(verifyResponse);
            setLoading(false);

            // Redirect to trainer portal after successful payment
            setTimeout(() => {
              navigate('/trainer');
            }, 3000);
          } else {
            setError(verifyResponse.Message || 'Payment verification failed. Please contact support.');
            setLoading(false);
          }
        } catch (err) {
          console.error('Error verifying trainer payment:', err);
          setError('Failed to verify payment: ' + (err.message || err.toString()));
          setLoading(false);
        }
        return;
      }

      // Handle session payment (client session payment)
      if (!sessionId) {
        setError('No payment session ID found');
        setLoading(false);
        return;
      }

      if (!requestId) {
        setError('Missing session request information. Please contact support if the payment was processed.');
        setLoading(false);
        return;
      }

      try {
        // Verify payment
        console.log('Verifying payment with session_id:', sessionId, 'request_id:', requestId);
        const verifyResponse = await apiService.verifyCheckoutSession({
          session_id: sessionId,
          request_id: requestId
        });

        console.log('Payment verification response:', verifyResponse);

        if (verifyResponse.Code === 200) {
          // Check if payment_status is 'completed' or if the response indicates success
          if (verifyResponse.payment_status === 'completed' || verifyResponse.Message === 'Payment verified') {
            setPaymentData(verifyResponse);
            setLoading(false);

            setTimeout(() => {
              navigate('/client-portal');
            }, 3000);
          } else {
            // Payment might still be processing - show a message but don't treat as error
            console.warn('Payment verification returned status:', verifyResponse.payment_status, 'Message:', verifyResponse.Message);
            setError(`Payment status: ${verifyResponse.payment_status || 'unknown'}. ${verifyResponse.Message || 'Payment may still be processing. Please check your session requests page.'}`);
            setLoading(false);
          }
        } else {
          const errorMsg = verifyResponse.Message || verifyResponse.error_details || verifyResponse.detail || 'Unknown error';
          console.error('Payment verification failed:', verifyResponse);
          setError('Payment verification failed: ' + errorMsg);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error processing payment:', err);
        setError('Failed to process payment: ' + (err.message || err.toString()));
      } finally {
        setLoading(false);
      }
    };

    processPayment();
  }, [searchParams, navigate]);

  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      color: 'white'
    },
    content: {
      background: 'white',
      borderRadius: '20px',
      padding: '40px',
      maxWidth: '500px',
      width: '100%',
      textAlign: 'center',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      color: '#2c3e50'
    },
    successIcon: {
      fontSize: '4rem',
      marginBottom: '20px'
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      marginBottom: '15px',
      color: '#27ae60'
    },
    message: {
      fontSize: '1.1rem',
      marginBottom: '30px',
      color: '#7f8c8d'
    },
    loading: {
      fontSize: '1.1rem',
      color: '#7f8c8d'
    },
    error: {
      color: '#e74c3c',
      fontSize: '1rem',
      marginTop: '20px'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.loading}>Processing payment...</div>
        </div>
      </div>
    );
  }

  if (error) {
    const trainerId = searchParams.get('trainer_id');
    const isTrainerPayment = trainerId && searchParams.get('payment') === 'success';
    const redirectPath = isTrainerPayment ? '/trainer' : '/client-portal';
    const redirectText = isTrainerPayment ? 'Go to Trainer Portal' : 'Go to Client Portal';
    const helpMessage = isTrainerPayment
      ? 'If you completed payment, please contact support if you continue to see this error.'
      : 'If you completed payment, please check your session requests page. The payment may still be processing.';

    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={{ ...styles.successIcon, color: '#e74c3c' }}>⚠️</div>
          <h1 style={{ ...styles.title, color: '#e74c3c' }}>Payment Status</h1>
          <p style={styles.error}>{error}</p>
          <p style={{ marginTop: '15px', fontSize: '0.9rem', color: '#7f8c8d' }}>
            {helpMessage}
          </p>
          <button
            onClick={() => navigate(redirectPath)}
            style={{
              marginTop: '20px',
              padding: '12px 30px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            {redirectText}
          </button>
        </div>
      </div>
    );
  }

  // Determine redirect message based on payment type
  const trainerId = searchParams.get('trainer_id');
  const isTrainerPayment = trainerId && searchParams.get('payment') === 'success';
  const redirectMessage = isTrainerPayment 
    ? 'Redirecting to trainer portal...' 
    : 'Redirecting to client portal...';
  const successMessage = isTrainerPayment
    ? 'Your registration payment has been processed successfully. Welcome to Pheefee!'
    : 'Your payment has been processed successfully. You can now join the session once the trainer is ready.';

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.successIcon}>✅</div>
        <h1 style={styles.title}>Payment Successful!</h1>
        <p style={styles.message}>
          {successMessage}
        </p>
        {paymentData && (
          <div style={{ marginTop: '20px', padding: '15px', background: '#ecf0f1', borderRadius: '8px' }}>
            <p style={{ margin: '5px 0', fontSize: '0.9rem' }}>
              <strong>Amount:</strong> ${paymentData.amount?.toFixed(2) || (isTrainerPayment ? '50.00' : '0.00')}
            </p>
            {paymentData.transaction_id && (
              <p style={{ margin: '5px 0', fontSize: '0.9rem' }}>
                <strong>Transaction ID:</strong> {paymentData.transaction_id?.substring(0, 20)}...
              </p>
            )}
          </div>
        )}
        <p style={{ marginTop: '20px', fontSize: '0.9rem', color: '#7f8c8d' }}>
          {redirectMessage}
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;


